import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { messengerActions } from 'srcReact/MessengerPage'
import useInfinitScroll from 'srcReact/helpers/hooks/useInfinitScroll'
import Common from './Common'
import Mobile from './Mobile'
import Desktop from './Desktop'
import usePrevious from 'srcReact/helpers/hooks/usePrevious'
import { Alert } from 'srcReact/components/common2/Alert'
import toast, { Toaster } from 'react-hot-toast'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'

export default function NewConversation({
  showUserLightModal,
  fromQuickMessenger,
  deviceMode,
  isMobile,
  setIsNewConvOpen,
  setSearchText,
  searchText,
  setConversationId
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    messenger: { searchedMembers, loading, searchedMemberStats, sidebarScreenType, openedConversation }
  } = useSelector((state) => state)
  const [membersToInvite, setMembersToInvite] = useState([])
  const [search, setSearch] = useState('')
  const [workplacesForConversation, setWorkplacesForConversation] = useState([])
  const [newConvCreated, setNewConvCreated] = useState(false)
  const [pagination, setPagination] = useState({
    nextOffset: 0,
    isPagination: false
  })
  const [organizationId, setOrganizationId] = useState(getCurrentOrgId())

  useEffect(() => {
    setSearch(searchText)
  }, [searchText])

  useEffect(() => {
    if (!organizationId) {
      setOrganizationId(getCurrentOrgId())
    }
  }, [])

  useEffect(() => {
    if (openedConversation && newConvCreated) {
      setNewConvCreated(false)
      if (setConversationId && fromQuickMessenger) {
        setConversationId(openedConversation.conversation.id)
      }

      if (isMobile || fromQuickMessenger) {
        dispatch(messengerActions.showMainContent(true))
        dispatch(messengerActions.showSidebar(false))
        dispatch(messengerActions.setSideBarScreenType('search'))
      } else {
        goToSearchScreen()
      }
    }
  }, [fromQuickMessenger, openedConversation, newConvCreated])

  useEffect(() => {
    if (previousSidebarScreen !== sidebarScreenType && searchedMembers?.length > 0) {
      return
    }
    if (organizationId > 0) {
      dispatch(messengerActions.searchMembers(search, organizationId, pagination))
    }
  }, [pagination, search])

  const startConversation = () => {
    if (membersToInvite?.length > 0) {
      const userIds = membersToInvite.map((m) => m.user_id).join(',')
      setNewConvCreated(true)
      dispatch(messengerActions.createConversation(organizationId, userIds))
    }
  }
  const startDirectConversation = (member) => {
    const userId = member?.user_id?.toString()
    dispatch(messengerActions.createConversation(organizationId, userId))
    if (isMobile) {
      dispatch(messengerActions.showMainContent(true))
      dispatch(messengerActions.showSidebar(false))
      dispatch(messengerActions.setSideBarScreenType('search'))
    } else {
      goToSearchScreen()
    }
    setSearchText('')
  }
  const resetState = () => {
    setSearch('')
    setPagination({
      nextOffset: 0,
      isPagination: false
    })
    setMembersToInvite([])
  }
  const handleSearch = (searchValue) => {
    if (searchValue?.length >= 2 || searchValue?.length === 0) {
      setPagination({
        nextOffset: 0,
        isPagination: false
      })
      setSearch(searchValue)
    }
  }
  const previousSidebarScreen = usePrevious(sidebarScreenType)
  const addMemberToInvite = (member) => {
    const workplaceIds = member.workplace_ids.split(',')

    if (membersToInvite?.length === 0) {
      setWorkplacesForConversation(workplaceIds)
      setMembersToInvite([...membersToInvite, member])
    } else {
      let isDiffWorkplace = true
      for (let i = 0; i < workplaceIds?.length; i++) {
        for (let j = 0; j < workplacesForConversation?.length; j++) {
          if (workplaceIds[i] === workplacesForConversation[j]) {
            isDiffWorkplace = false
            break
          }
        }
      }
      if (isDiffWorkplace) {
        toast.custom((toastProps) => (
          <Alert text={t('messenger.clientErrors.sameWorkpalceConvo')} type='error' toastProps={toastProps} />
        ))
      } else {
        setMembersToInvite([...membersToInvite, member])
      }
    }

    if (search?.length > 0) {
      setSearch('')
    }
  }
  const removeMemberFromInvite = (member) => {
    if (membersToInvite?.length === 1) {
      setWorkplacesForConversation([])
    }
    const filteredMembers = membersToInvite.filter((m) => m.user_id !== member.user_id)
    setMembersToInvite(filteredMembers)
  }
  const lastMemberRef = useInfinitScroll(
    loading,
    searchedMemberStats && searchedMemberStats.remaining,
    searchedMemberStats && searchedMemberStats.nextOffset,
    setPagination,
    [loading]
  )
  const goToSearchScreen = () => {
    dispatch(messengerActions.setSideBarScreenType('search'))
  }

  return (
    <React.Fragment>
      {deviceMode === 'common' ? (
        <Common
          membersToInvite={membersToInvite}
          lastMemberRef={lastMemberRef}
          search={search}
          goToSearchScreen={goToSearchScreen}
          startDirectConversation={startDirectConversation}
          removeMemberFromInvite={removeMemberFromInvite}
          addMemberToInvite={addMemberToInvite}
          showUserLightModal={showUserLightModal}
          isMemberAddedForInvitation={isMemberAddedForInvitation}
          handleSearch={handleSearch}
        />
      ) : deviceMode === 'mobile' ? (
        <Mobile
          membersToInvite={membersToInvite}
          lastMemberRef={lastMemberRef}
          search={search}
          goToSearchScreen={goToSearchScreen}
          startConversation={startConversation}
          removeMemberFromInvite={removeMemberFromInvite}
          addMemberToInvite={addMemberToInvite}
          showUserLightModal={showUserLightModal}
          isMemberAddedForInvitation={isMemberAddedForInvitation}
          handleSearch={handleSearch}
          setIsNewConvOpen={setIsNewConvOpen}
        />
      ) : (
        <Desktop
          membersToInvite={membersToInvite}
          lastMemberRef={lastMemberRef}
          search={search}
          goToSearchScreen={goToSearchScreen}
          startConversation={startConversation}
          removeMemberFromInvite={removeMemberFromInvite}
          addMemberToInvite={addMemberToInvite}
          showUserLightModal={showUserLightModal}
          isMemberAddedForInvitation={isMemberAddedForInvitation}
          handleSearch={handleSearch}
        />
      )}
      <Toaster />
    </React.Fragment>
  )
}

function isMemberAddedForInvitation(members, member) {
  return members.some((m) => m.user_id === member.user_id)
}
